import { getEmojiPath, getEmoji } from "../../../utils/emojies";

type EmojiSize = "hero-xl" | "hero-lg" | "hero-md" | "large-xl" | "large" | "medium" | "custom";

interface EmojiProps {
  name: string;
  alt?: string;
  className?: string;
  width?: number | string;
  height?: number | string;
  fallback?: string;
  /**
   * Preset size following the design system guidelines
   * - hero-xl: 400px (for major hero sections)
   * - hero-lg: 300px (for section heroes)
   * - hero-md: 200px (for standard large usage)
   * - large-xl: 150px (for section elements)
   * - large: 100px (minimum recommended size)
   * - medium: 80px (use with caution, below 100px)
   * - custom: use className for custom sizing
   */
  size?: EmojiSize;
  /**
   * Enable responsive sizing (works with preset sizes)
   * Automatically scales down on mobile devices
   */
  responsive?: boolean;
}

const sizeClasses: Record<EmojiSize, string> = {
  "hero-xl": "w-[400px] h-[400px]",
  "hero-lg": "w-[300px] h-[300px]",
  "hero-md": "w-[200px] h-[200px]",
  "large-xl": "w-[150px] h-[150px]",
  "large": "w-[100px] h-[100px]",
  "medium": "w-20 h-20", // 80px - use sparingly
  "custom": "",
};

const responsiveSizeClasses: Record<EmojiSize, string> = {
  "hero-xl": "w-[200px] h-[200px] md:w-[300px] md:h-[300px] lg:w-[400px] lg:h-[400px]",
  "hero-lg": "w-[150px] h-[150px] md:w-[220px] md:h-[220px] lg:w-[300px] lg:h-[300px]",
  "hero-md": "w-[120px] h-[120px] md:w-[160px] md:h-[160px] lg:w-[200px] lg:h-[200px]",
  "large-xl": "w-[100px] h-[100px] md:w-[130px] md:h-[130px] lg:w-[150px] lg:h-[150px]",
  "large": "w-[100px] h-[100px]", // Already at minimum, no scaling down
  "medium": "w-16 h-16 md:w-20 md:h-20", // 64px to 80px
  "custom": "",
};

/**
 * Emoji component that displays emoji images from /public/emojies/
 * 
 * @example
 * // Using preset sizes (recommended)
 * <Emoji name="cool" size="hero-lg" alt="Cool emoji" />
 * <Emoji name="happy-energy" size="large" responsive alt="Happy energy" />
 * 
 * // Using custom className
 * <Emoji name="cool" size="custom" className="w-12 h-12" alt="Cool emoji" />
 * 
 * // Using width/height props (legacy)
 * <Emoji name="happy-energy" width={200} height={200} alt="Happy energy" />
 */
export default function Emoji({
  name,
  alt,
  className = "",
  width,
  height,
  fallback,
  size = "custom",
  responsive = false,
}: EmojiProps) {
  const emojiPath = getEmojiPath(name);
  const emoji = getEmoji(name);
  const altText = alt || emoji?.description || `${name} emoji`;

  // Determine size classes
  const sizeClass = responsive && size !== "custom" 
    ? responsiveSizeClasses[size] 
    : sizeClasses[size];

  // Combine classes: size class + custom className
  const combinedClassName = size !== "custom" 
    ? `${sizeClass} ${className}`.trim()
    : className;

  if (!emojiPath) {
    // Fallback to text if emoji not found
    return (
      <span className={combinedClassName} aria-label={altText}>
        {fallback || name}
      </span>
    );
  }

  return (
    <img
      src={emojiPath}
      alt={altText}
      className={combinedClassName}
      width={width}
      height={height}
      loading="lazy"
      style={{
        objectFit: "contain",
        ...(width ? { width } : {}),
        ...(height ? { height } : {}),
      }}
    />
  );
}

